import { Injectable, signal } from "@angular/core";
import { ProfileService } from "./profile.service";
import { ConfigService } from "./config.service";
import { environment } from "../../environments/environment";
import { AuthService } from "./auth.service";
import { RoutingService } from "./routing.service";
import { ApiService } from "./api.service";
import { UtilService } from "./util.service";
import { BvDialogService } from "../design-system";
import { CheckoutComponent } from "../subscription/checkout/checkout.component";
import { PricingCatalogService } from "./pricing-catalog.service";

@Injectable()
export class SubscriptionService {
  card: any;
  stripe: any;
  // Set in Stripe .then callbacks (createPaymentMethod/confirmCardPayment) and
  // read in credit-card/profile templates → signal-backed so the error shows
  // under zoneless (get/set keeps all `th.cardError = …` sites unchanged).
  private _cardError = signal<string | null>(null);
  get cardError(): string | null { return this._cardError(); }
  set cardError(v: string | null) { this._cardError.set(v); }
  latestProration: any;
  openPayInv: boolean = false;
  latestCard: any;
  constructor(
    public configServ: ConfigService,
    public profServ: ProfileService,
    private auth: AuthService,
    private routing: RoutingService,
    private dialog: BvDialogService,
    private api: ApiService,
    private util: UtilService,
    private pricingCatalog: PricingCatalogService
  ) {}
  initStripe() {
    this.stripe = (<any>window).Stripe(environment.stripeClientKey);
    let elements = this.stripe.elements();
    // Create an instance of the card Element
    this.card = elements.create("card", {
      iconStyle: "solid",
      style: {
        base: {
          iconColor: "#F99A52",
          color: "#ffffff",
          lineHeight: "32px",
          fontWeight: 400,
          fontFamily: '"Open Sans", sans-serif',
          fontSize: "16px",

          "::placeholder": {
            color: "#888",
          },
        },
        invalid: {
          iconColor: "#e85746",
          color: "#e85746",
        },
      },
      classes: {
        focus: "is-focused",
        empty: "is-empty",
      },
    });
  }
  /**
   * True when the user has an existing sub whose currency differs from the
   * geo-resolved catalog currency. In that case the upgrade can't go through
   * changeSubscription (Stripe pins Customers to a single currency at first
   * invoice) — it has to cancel + refund + recreate, which is what
   * convertSubscriptionCurrency does.
   */
  isCurrencyChange(): boolean {
    const subCurrency = (
      (this.profServ.latestProfile &&
        this.profServ.latestProfile.subscription &&
        this.profServ.latestProfile.subscription.plan &&
        this.profServ.latestProfile.subscription.plan.currency) ||
      ""
    )
      .toString()
      .toUpperCase();
    const geoCurrency = (
      (this.pricingCatalog.getCached() &&
        this.pricingCatalog.getCached()!.currency) ||
      ""
    )
      .toString()
      .toUpperCase();
    return !!(subCurrency && geoCurrency && subCurrency !== geoCurrency);
  }
  subscribe(plan: any, interval: string, coupon?: string) {
    if (this.isCurrencyChange()) {
      this.convertCurrencyAndSubscribe(plan, interval);
      return;
    }
    this.profServ.sub_processing = true;
    this.cardError = null;
    let change: any = {
      plan: plan.code,
      interval: interval,
      currency: this.profServ.latestProfile.currency,
      level: plan.level,
      player_limit: plan.player_limit,
    };
    if (this.profServ.latestProfile.vat && this.profServ.amountDue > 0)
      change.vat = this.profServ.latestProfile.vat;
    if (coupon) change.coupon = coupon;
    if (this.latestProration) {
      change.proration_date = this.latestProration.proration_date;
      change.sub_item_id =
        this.profServ.latestProfile.subscription.items.data[0].id;
    }
    //		if (!this.profServ.latestProfile.stripeCards || this.profServ.latestProfile.subscription_card_declined) {
    let th = this;
    if (
      !this.profServ.latestProfile.stripeCards ||
      this.profServ.latestProfile.subscription_card_declined
    ) {
      this.stripe
        .createPaymentMethod({ type: "card", card: this.card })
        .then(function (result: any) {
          if (result.error) {
            th.cardError = result.error.message;
            th.profServ.sub_processing = null;
          } else {
            th.latestCard = result.paymentMethod.card;
            change.pm = result.paymentMethod.id;
            th.api.changeSubscription(th.slimProfile(), change).then((outerResult) => {
              if (th.util.isError(outerResult)) {
                th.util.errorDialog(outerResult.data);
                th.profServ.sub_processing = null;
              } else {
                // Apply optimistic update for both branches: the server has
                // already written the sub to /profiles/{uid}/subscription
                // before responding, so we can show the new plan info
                // immediately rather than waiting on the FB listener.
                th.applyOptimisticSub(outerResult);
                if (
                  outerResult.data &&
                  outerResult.data.action &&
                  outerResult.data.action == "handlePayment"
                )
                  th.stripe
                    .confirmCardPayment(outerResult.data.piSecret)
                    .then(async (scaResult: any) => {
                      if (scaResult.error) {
                        th.util.errorDialog(scaResult.error);
                        th.profServ.sub_processing = null;
                      } else {
                        await th.refreshAfterSca(outerResult);
                        th.postSubProcessing();
                      }
                    });
                else th.postSubProcessing();
              }
            });
          }
        });
    } else
      this.api.changeSubscription(this.slimProfile(), change).then((outerResult) => {
        if (th.util.isError(outerResult)) {
          th.util.errorDialog(outerResult.data);
          th.profServ.sub_processing = null;
        } else {
          th.applyOptimisticSub(outerResult);
          if (
            outerResult.data &&
            outerResult.data.action &&
            outerResult.data.action == "handlePayment"
          )
            th.stripe.confirmCardPayment(outerResult.data.piSecret).then(async (scaResult: any) => {
              if (scaResult.error) {
                th.util.errorDialog(scaResult.error);
                th.profServ.sub_processing = null;
              } else {
                await th.refreshAfterSca(outerResult);
                th.postSubProcessing();
              }
            });
          else th.postSubProcessing();
        }
      });
  }

  /**
   * Cross-currency upgrade. Drives the convertSubscriptionCurrency callable
   * (cancel old → refund → create new sub on the same Customer in the geo
   * currency). The checkout summary already showed the refund + new charge
   * to the user, so no extra confirmation is needed here.
   */
  private async convertCurrencyAndSubscribe(plan: any, interval: string) {
    this.profServ.sub_processing = true;
    const result: any = await this.api.convertSubscriptionCurrency(
      plan.code,
      interval,
      plan.level,
      plan.player_limit
    );
    if (!result || !result.data || this.util.isError(result.data)) {
      this.util.errorDialog(
        (result && result.data) || {
          code: "convert_failed",
          message: "Conversion failed",
        }
      );
      this.profServ.sub_processing = null;
      return;
    }
    this.applyOptimisticSub(result);
    if (result.data.action === "handlePayment") {
      const scaResult: any = await this.stripe.confirmCardPayment(
        result.data.piSecret
      );
      if (scaResult.error) {
        this.util.errorDialog(scaResult.error);
        this.profServ.sub_processing = null;
        return;
      }
      await this.refreshAfterSca(result);
    }
    this.postSubProcessing();
  }

  /**
   * Populate profServ.latestSubscription from the changeSubscription server
   * response so the profile/subscription view renders immediately, without
   * waiting for the Firebase realtime listener to push the update. The server
   * has already written the same object to /profiles/{uid}/subscription, so
   * the listener firing milliseconds later just re-sets the same value.
   */
  private applyOptimisticSub(result: any) {
    const sub = result && result.data && result.data.subscription;
    if (sub) this.profServ.latestSubscription = sub;
  }

  /**
   * After a successful SCA / 3DS confirmCardPayment, the sub transitions
   * from `incomplete` to `active` server-side, but the
   * invoice.payment_succeeded webhook that mirrors the new state to FB
   * arrives 1–4s later. We await refreshSubscription instead so the loading
   * spinner stays on until the active sub object is in hand, then apply it
   * optimistically before postSubProcessing routes to the profile screen.
   */
  private async refreshAfterSca(outerResult: any) {
    const sub = outerResult && outerResult.data && outerResult.data.subscription;
    if (!sub || !sub.id) return;
    const refreshed = await this.api.refreshSubscription(this.profServ.uid!, sub.id);
    this.applyOptimisticSub(refreshed);
  }

  postSubProcessing() {
    this.profServ.sub_processing = false;
    this.dialog.closeAll();
    this.routing.openSub = true;
    this.routing.toLobby();
  }
  slimProfile() {
    let slim: any = {
      uid: this.profServ.latestProfile.uid,
    };

    if (this.profServ.latestProfile.stripeID)
      slim.stripeID = this.profServ.latestProfile.stripeID;
    if (this.profServ.latestProfile.subscription)
      slim.subscription = this.profServ.latestProfile.subscription;
    if (this.profServ.latestProfile.locationData)
      slim.locationData = this.profServ.latestProfile.locationData;
    if (this.profServ.latestProfile.email)
      slim.email = this.profServ.latestProfile.email;
    return slim;
  }
  prorate(planIndex: number) {
    this.profServ.sub_processing = true;
    let plan = this.configServ.getPlanByIndex(planIndex);
    let change: any = {
      plan: plan.code,
      interval: this.profServ.latestProfile.subInterval,
      currency: this.profServ.latestProfile.currency,
      level: plan.level,
      player_limit: plan.player_limit,
      sub_item_id: this.profServ.latestProfile.subscription.items.data[0].id,
    };
    let prorateProm: Promise<any> = this.api.prorate(
      this.slimProfile(),
      change
    );
    let th: any = this;
    prorateProm.then((result) => {
      if (th.util.isError(result)) {
        th.util.errorDialog(result.data);
        th.profServ.sub_processing = null;
      } else {
        th.latestProration = result.data;
        th.profServ.sub_processing = false;
      }
    });
    return prorateProm;
  }

  cancelSubscription() {
    this.profServ.sub_processing = true;
    let th: any = this;
    this.api
      .cancelSubscription(
        this.profServ.uid!,
        this.profServ.latestSubscription.id,
        this.profServ.latestSubscription.code
      )
      .then((result) => {
        if (th.util.isError(result)) {
          th.util.errorDialog(result.data);
          th.profServ.sub_processing = null;
        } else {
          th.applyOptimisticSub(result);
          th.profServ.sub_processing = false;
        }
      });
  }
  reactivateSubscription() {
    this.profServ.sub_processing = true;
    let th: any = this;
    const sub = this.profServ.latestSubscription;
    const subItemId = sub.items && sub.items.data && sub.items.data[0] && sub.items.data[0].id;
    this.api
      .reactivateSubscription(
        this.profServ.uid!,
        sub.id,
        sub.plan.id,
        sub.code,
        subItemId
      )
      .then((result) => {
        if (th.util.isError(result)) {
          th.util.errorDialog(result.data);
          th.profServ.sub_processing = null;
        } else {
          th.applyOptimisticSub(result);
          th.profServ.sub_processing = false;
        }
      });
  }
  payInvoice() {
    this.profServ.sub_processing = true;
    let th = this;
    this.stripe
      .createPaymentMethod({ type: "card", card: this.card })
      .then(function (result: any) {
        if (result.error) {
          th.cardError = result.error.message;
          th.profServ.sub_processing = null;
        } else {
          th.cardError = null;
          th.api
            .payInvoice(
              th.profServ.uid!,
              th.profServ.latestProfile.stripeID,
              result.paymentMethod.id
            )
            .then((result) => {
              if (th.util.isError(result)) {
                th.util.errorDialog(result.data);
                th.profServ.sub_processing = null;
              } else th.postSubProcessing();
            });
        }
      });
  }
  addCard(uid: string) {
    this.profServ.sub_processing = true;
    let th = this;
    this.stripe
      .createPaymentMethod({ type: "card", card: this.card })
      .then(function (result: any) {
        if (result.error) {
          th.cardError = result.error.message;
          th.profServ.sub_processing = null;
        } else {
          th.cardError = null;
          th.api
            .addCard(uid, th.profServ.latestProfile.stripeID, result.paymentMethod.id)
            .then((result) => {
              if (th.util.isError(result)) {
                th.util.errorDialog(result.data);
                th.profServ.sub_processing = null;
              } else {
                th.profServ.sub_processing = null;
                th.card.clear();
              }
            });
        }
      });
  }
  deleteCard(cardID: string) {
    this.profServ.sub_processing = true;
    let th = this;
    this.api
      .deleteCard(
        this.profServ.uid!,
        this.profServ.latestProfile.stripeID,
        cardID
      )
      .then((result) => {
        if (th.util.isError(result)) {
          th.util.errorDialog(result.data);
          th.profServ.sub_processing = null;
        } else th.profServ.sub_processing = null;
      });
  }
  paymentIcon(brand: string) {
    switch (brand) {
      case "American Express":
        return "/assets/img/payment/americanexpress.png";
      case "Diners Club":
        return "/assets/img/payment/dinersclub.png";
      case "Discover":
        return "/assets/img/payment/discover.png";
      case "JCB":
        return "/assets/img/payment/jcb.png";
      case "MasterCard":
        return "/assets/img/payment/mastercard.png";
      case "Visa":
        return "/assets/img/payment/visa.png";
      default:
        return "none";
    }
  }
  changeDefaultCard(cardID: string) {
    this.profServ.sub_processing = true;
    let th = this;
    this.api
      .changeDefaultCard(
        this.profServ.uid!,
        this.profServ.latestProfile.stripeID,
        cardID
      )
      .then((result) => {
        if (th.util.isError(result)) {
          th.util.errorDialog(result.data);
          th.profServ.sub_processing = null;
        } else th.profServ.sub_processing = null;
      });
  }
}
